import { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHotkeys } from 'react-hotkeys-hook'
import { Draggable } from 'apps/studio/components/dragAndDrop/Draggable'
import { FieldIcon } from 'apps/studio/components/shared/FieldIcon'
import {
  updateElement,
  selectElement,
} from 'apps/studio/store/actions/sharedActions'
import { ResizeHandle } from 'apps/studio/components/pdfViewer/elements/ResizeHandle'
import {
  entriesSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import {
  activeEntryGuidSelector,
  activeModalNameSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import { elementsSelector } from 'apps/studio/store/selectors/pdf'
import { pdfPageMetaByPageNumberSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import { styleDefaults } from 'apps/studio/lib/utils/styles'
import { elementAlertConfig } from 'apps/studio/lib/utils/element'
import { selectActiveEntryLabel } from 'apps/studio/lib/utils/dnd'
import { appSettingsActions } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
const { toggleModal } = appSettingsActions
import { arePropsEqual } from 'apps/studio/lib/utils/compareElement'
import { IconTooltip } from 'apps/studio/components/shared/IconTooltip'
import { WarningIcon } from '@gocanvas/gcds/icons'
import {
  getDeleteElementAction,
  getDuplicateElementAction,
} from 'apps/studio/lib/utils/entities'

const ELEMENT_PROPS = [
  'entry_guid',
  ['position', 'x'],
  ['position', 'y'],
  ['position', 'width'],
  ['position', 'height'],
]

const STYLE_PROPS = [
  'backgroundColor',
  'backgroundColorOpacity',
  'fontColor',
  'fontFamily',
  'fontSize',
  'horizontalAlignment',
  'verticalAlignment',
  'lineHeight',
  'fontWeight',
  'fontStyle',
  'textDecoration',
]

const PAGE_PROPS = ['id', 'page_number', 'section_ids']

const LAYOUT_PROPS = ['layout_type', 'columns']

const hexToRGBString = hex => {
  const bigint = parseInt(hex.replace(/^#/, ''), 16)
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`
}

const convertToFlexAlignment = alignment => {
  switch (alignment) {
    case 'top':
    case 'left':
      return 'flex-start'
    case 'bottom':
    case 'right':
      return 'flex-end'
    case 'center':
    default:
      return 'center'
  }
}

export const generateStyles = ({ position, styles }) => {
  const { x, y, width, height } = position
  const computedStyles = {
    ...styleDefaults,
    ...styles,
  }
  const {
    backgroundColor,
    backgroundColorOpacity,
    fontColor,
    fontFamily,
    fontSize,
    horizontalAlignment,
    verticalAlignment,
    lineHeight,
    fontWeight,
    fontStyle,
    textDecoration,
  } = computedStyles

  const bgColor =
    backgroundColor && backgroundColorOpacity !== undefined
      ? `rgba(${hexToRGBString(backgroundColor)}, ${backgroundColorOpacity})`
      : backgroundColor && backgroundColor

  return {
    '--element--position-x': `${x}px`,
    '--element--position-y': `${y}px`,
    '--element--width': `${width}px`,
    '--element--height': `${height}px`,
    '--element--background-color': bgColor,
    '--element--color': fontColor && fontColor,
    '--element--font-size': `${fontSize}px`,
    '--element--font-family': fontFamily,
    '--element--text-align': horizontalAlignment && horizontalAlignment,
    '--element--horizontal-align':
      horizontalAlignment && convertToFlexAlignment(horizontalAlignment),
    '--element--vertical-align':
      verticalAlignment && convertToFlexAlignment(verticalAlignment),
    '--element--max-lines': Math.floor(height / (lineHeight * fontSize)),
    '--element--font-weight': fontWeight,
    '--element--font-style': fontStyle,
    '--element--text-decoration': textDecoration,
  }
}

export const PdfElementAlert = ({ type, message, direction = 'top' }) => {
  const alertIcons = {
    warning: WarningIcon,
  }

  return (
    <div className={`element--notice element--${type}`}>
      <IconTooltip
        Icon={alertIcons[type]}
        tooltipContent={message}
        position={direction}
      />
    </div>
  )
}

// movement keyMap
const moveField = ({
  key,
  shiftKey,
  ctrlKey,
  position,
  pageDimensions,
  speed = 1,
}) => {
  const { x, y, width, height } = position
  const { originalHeight, originalWidth } = pageDimensions
  const moveBy = shiftKey ? Math.min(speed * 10, 100) : Math.min(speed, 20)
  const keyMap = {
    ArrowUp: { y: ctrlKey ? 0 : Math.max(y - moveBy, 0) },
    ArrowDown: {
      y: ctrlKey
        ? originalHeight - height
        : Math.min(y + moveBy, originalHeight - height),
    },
    ArrowLeft: { x: ctrlKey ? 0 : Math.max(x - moveBy, 0) },
    ArrowRight: {
      x: ctrlKey
        ? originalWidth - width
        : Math.min(x + moveBy, originalWidth - width),
    },
  }
  return keyMap[key]
}

export const PdfElement = React.memo(
  ({ element, page, layout }) => {
    const { position, entry_guid, styles } = element
    const { page_number } = page
    const { columns = [], layout_type } = layout
    const activeModal = useSelector(activeModalNameSelector)
    const dispatch = useDispatch()
    const elements = useSelector(elementsSelector)
    const entries = useSelector(entriesSelector)
    const sheets = useSelector(sheetsSelector)
    const pageDimensions =
      useSelector(pdfPageMetaByPageNumberSelector(page_number)) || {}
    const entry = entries.find(entry => entry.guid === entry_guid)
    const { label, sheet_id, type, keyField } = entry
    const activeEntryGuid = useSelector(activeEntryGuidSelector)
    const isActiveElement = activeEntryGuid === entry_guid
    const isInTable = layout_type === 'table'
    const elementStyles = generateStyles({ position, styles })
    const elementAlertData = elementAlertConfig[type] || {}
    const speedRef = useRef(1)
    const repeatCountRef = useRef(0)
    const requestRef = useRef(null)
    const {
      alertType,
      alertMessage,
      actionRequired = () => false,
    } = elementAlertData

    const showElementAlert = actionRequired(entry)

    useHotkeys(
      'esc',
      () => {
        if (!activeModal) {
          dispatch(
            selectElement({
              entry_guid: null,
              sheet_id: null,
            })
          )
        } else {
          dispatch(toggleModal({ activeModal: '' }))
        }
      },
      [activeEntryGuid, activeModal]
    )

    useHotkeys(
      'delete, backspace',
      () => {
        if (
          isActiveElement &&
          !keyField &&
          !(isInTable && columns.length <= 2)
        ) {
          const action = getDeleteElementAction({
            element,
            layout,
            sheets,
            entries,
            elements,
          })
          dispatch(action)
        }
      },
      [activeEntryGuid, layout]
    )

    useHotkeys(
      'ctrl+d, cmd+d',
      e => {
        if (isActiveElement && !keyField) {
          e.preventDefault()
          const action = getDuplicateElementAction({
            element,
            layout,
            sheets,
            entry,
            entries,
            elements,
            pdfPageMeta: pageDimensions,
          })
          dispatch(action)
        }
      },
      [activeEntryGuid, position, layout]
    )

    const handleKeyDown = e => {
      if (e.repeat) {
        repeatCountRef.current++
        speedRef.current = ~~Math.min(
          15,
          1 + Math.pow(repeatCountRef.current, 1.5) / 5
        )
      } else {
        repeatCountRef.current = 0
        speedRef.current = 1
      }
      if (activeEntryGuid === entry_guid) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current)
        }
        requestRef.current = requestAnimationFrame(() => {
          const newPosition = moveField({
            key: e.key,
            shiftKey: e.shiftKey,
            ctrlKey: e.ctrlKey,
            position,
            pageDimensions,
            speed: speedRef.current,
          })
          if (newPosition) {
            dispatch(
              updateElement({
                entry,
                element: {
                  entry_guid,
                  position: { ...position, ...newPosition },
                },
              })
            )
          }
        })
      }
    }

    useHotkeys(
      'up, down, left, right, shift+up, shift+down, shift+left, shift+right, shift+ctrl+up, shift+ctrl+down, shift+ctrl+left, shift+ctrl+right',
      handleKeyDown,
      [activeEntryGuid, position]
    )

    const setActive = e => {
      dispatch(
        selectElement({
          entry_guid,
          sheet_id,
        })
      )
      e.stopPropagation()
    }

    const onDoubleClick = e => {
      e.preventDefault()
      e.stopPropagation()
      selectActiveEntryLabel(type, entry_guid)
    }

    const wrapperId = `page-element--wrapper-${entry_guid}`
    const resizeHandleProps = {
      wrapperId,
      position,
      entry,
    }

    return (
      <div
        id={wrapperId}
        className={`page-element--wrapper${
          isInTable ? ' page-element--in-table' : ''
        }`}
        style={elementStyles}
      >
        <ResizeHandle handleLocation="top-left" {...resizeHandleProps} />
        <ResizeHandle handleLocation="top-right" {...resizeHandleProps} />
        <Draggable
          id={`${element.entry_guid}-draggable`}
          // disable drag to stop a column being dragged out of the table
          disabled={isInTable}
          data={{
            type: 'pageElement',
            page,
            layout,
            element: { ...element, styles: elementStyles, label },
          }}
        >
          <div
            className={`page-element 
            ${isActiveElement ? 'element--is-active' : ''}
            ${showElementAlert ? `element--has-${alertType}` : ''}
          `}
            onClick={setActive}
            onDoubleClick={onDoubleClick}
          >
            <div className="element--label workspace-element--label">
              <FieldIcon type={type} keyField={keyField} />
              <span className="element--label-name">{label}</span>
              {showElementAlert && (
                <PdfElementAlert type={alertType} message={alertMessage} />
              )}
            </div>
          </div>
        </Draggable>
        <ResizeHandle handleLocation="bottom-left" {...resizeHandleProps} />
        <ResizeHandle handleLocation="bottom-right" {...resizeHandleProps} />
      </div>
    )
  },
  (prevProps, nextProps) => {
    const elementEqual = arePropsEqual(
      prevProps.element,
      nextProps.element,
      ELEMENT_PROPS
    )

    const prevStyles = prevProps.element.styles || {}
    const nextStyles = nextProps.element.styles || {}
    const stylesEqual = arePropsEqual(prevStyles, nextStyles, STYLE_PROPS)

    const pageEqual = arePropsEqual(prevProps.page, nextProps.page, PAGE_PROPS)

    const layoutEqual = arePropsEqual(
      prevProps.layout,
      nextProps.layout,
      LAYOUT_PROPS
    )

    return elementEqual && stylesEqual && pageEqual && layoutEqual
  }
)
